import { useActiveElement } from '@vueuse/core';
import { ChameleonState, ChatState } from '@/models/chat';

export const useChatInfoBar = () => {
  const { t } = useI18n();

  const chatStore = useChatStore();
  const { chatState, chameleonState, isChameleonMode, foundCompanyName } = storeToRefs(chatStore);

  const inputRef = ref<HTMLInputElement | null>(null);
  const isExpanded = ref(false);
  const isOpen = ref(false);

  const companyName = ref('');
  const isSubmitDisabled = computed(() => !companyName.value.length || chameleonState.value !== ChameleonState.Idle);
  const isInputDisabled = computed(
    () => chameleonState.value !== ChameleonState.Idle || (chatState.value !== ChatState.Home && chatState.value !== ChatState.Chat)
  );
  const infoBarText = computed(() =>
    isChameleonMode.value ? (foundCompanyName.value ? foundCompanyName.value : companyName.value) : t('chat.infoBar.text')
  );

  const openInput = () => {
    if (chameleonState.value !== ChameleonState.Idle) return;

    companyName.value = '';
    isOpen.value = true;

    setTimeout(() => {
      focusInput();
    }, 500);

    expandBar();
  };

  const closeInput = () => {
    isOpen.value = false;
  };

  const focusInput = () => {
    inputRef.value?.focus();
  };

  const expandBar = () => {
    isExpanded.value = true;
  };

  const closeBar = () => {
    isExpanded.value = false;

    if (!companyName.value.length) closeInput();
  };

  const onBlur = () => {
    const activeElement = useActiveElement();

    if (
      !activeElement.value ||
      activeElement.value.classList.contains('chat-info-bar') ||
      activeElement.value.classList.contains('chat-info-bar__input') ||
      activeElement.value.classList.contains('chat-info-bar__text') ||
      activeElement.value.classList.contains('chat-info-bar__btn-submit')
    ) {
      return;
    }

    closeBar();
  };

  const onSubmit = async () => {
    if (isSubmitDisabled.value) return;

    try {
      await chatStore.searchByCompanyName(companyName.value);
      chatState.value = ChatState.Chat;
    } catch {
      // do nothing
    }
  };

  const onKeyPress = (e: KeyboardEvent) => {
    if (e.key !== 'Enter') return;

    onSubmit();
  };

  watch(isChameleonMode, (value) => {
    if (!value) return;

    closeInput();
    closeBar();
  });

  return {
    inputRef,
    infoBarText,
    isExpanded,
    isOpen,
    companyName,
    isSubmitDisabled,
    isInputDisabled,
    chameleonState,
    openInput,
    closeInput,
    focusInput,
    expandBar,
    closeBar,
    onBlur,
    onSubmit,
    onKeyPress
  };
};
